import React, { useState, useEffect, useCallback } from 'react';

const API_BASE = '';

function AnalyticsTab({ adminToken }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [days, setDays] = useState('30');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API_BASE}/api/admin/analytics?admin_token=${adminToken}&days=${days}`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setData(await res.json());
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [adminToken, days]);

  useEffect(() => { load(); }, [load]);

  const barColor = '#a855f7';
  const maxViews = data?.dailyStats?.reduce((m, d) => Math.max(m, d.total_views), 1) || 1;

  return (
    <div style={{ color: '#fff' }}>
      {/* Header + Filter */}
      <div style={{ display: 'flex', gap: '1rem', alignItems: 'center', marginBottom: '1.5rem', flexWrap: 'wrap' }}>
        <h3 style={{ margin: 0, fontSize: '1.25rem' }}>📊 Besucher-Analyse</h3>
        <select
          value={days}
          onChange={e => setDays(e.target.value)}
          style={{ background: '#1e1b4b', color: '#fff', border: '1px solid #a855f7', borderRadius: '0.375rem', padding: '0.4rem 0.75rem', cursor: 'pointer' }}
        >
          <option value="7">Letzte 7 Tage</option>
          <option value="30">Letzte 30 Tage</option>
          <option value="90">Letzte 90 Tage</option>
          <option value="365">Letztes Jahr</option>
        </select>
        <button
          onClick={load}
          style={{ background: 'rgba(168,85,247,0.3)', border: '1px solid #a855f7', color: '#fff', padding: '0.4rem 1rem', borderRadius: '0.375rem', cursor: 'pointer' }}
        >
          🔄 Aktualisieren
        </button>
      </div>

      {loading && <p style={{ color: '#94a3b8' }}>Lade Daten…</p>}
      {error && <p style={{ color: '#f87171' }}>Fehler: {error}</p>}

      {data && !loading && (
        <>
          {/* KPI Cards */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '1rem', marginBottom: '2rem' }}>
            {[
              { label: 'Einzigartige Besucher', value: data.totals?.unique_visitors ?? 0, icon: '👤' },
              { label: 'Seitenaufrufe gesamt', value: data.totals?.total_views ?? 0, icon: '👁️' },
              { label: 'Tage mit Besuchern', value: data.dailyStats?.filter(d => d.total_views > 0).length ?? 0, icon: '📅' },
            ].map(({ label, value, icon }) => (
              <div key={label} style={{ background: 'rgba(168,85,247,0.15)', border: '1px solid rgba(168,85,247,0.4)', borderRadius: '0.5rem', padding: '1rem', textAlign: 'center' }}>
                <div style={{ fontSize: '1.75rem' }}>{icon}</div>
                <div style={{ fontSize: '1.75rem', fontWeight: 'bold', color: '#e9d5ff' }}>{value}</div>
                <div style={{ fontSize: '0.75rem', color: '#94a3b8', marginTop: '0.25rem' }}>{label}</div>
              </div>
            ))}
          </div>

          {/* Daily bar chart */}
          <div style={{ marginBottom: '2rem' }}>
            <h4 style={{ color: '#c4b5fd', marginBottom: '0.75rem', fontSize: '0.9rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              Seitenaufrufe pro Tag
            </h4>
            {data.dailyStats?.length === 0 ? (
              <p style={{ color: '#64748b', fontStyle: 'italic' }}>Noch keine Daten für diesen Zeitraum.</p>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <div style={{ display: 'flex', alignItems: 'flex-end', gap: '4px', minHeight: '120px', paddingBottom: '2rem', position: 'relative' }}>
                  {data.dailyStats?.map(day => {
                    const pct = Math.max(4, Math.round((day.total_views / maxViews) * 100));
                    return (
                      <div key={day.day} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flex: '1', minWidth: '24px' }} title={`${day.day}: ${day.total_views} Aufrufe, ${day.unique_visitors} Besucher`}>
                        <span style={{ fontSize: '0.6rem', color: '#94a3b8', marginBottom: '2px' }}>{day.total_views}</span>
                        <div style={{ width: '100%', height: `${pct}px`, background: barColor, borderRadius: '2px 2px 0 0', opacity: 0.85, transition: 'height 0.3s' }} />
                        <span style={{ fontSize: '0.55rem', color: '#64748b', marginTop: '4px', writingMode: 'vertical-rl', transform: 'rotate(180deg)', whiteSpace: 'nowrap' }}>
                          {day.day?.slice(5)}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          {/* Referrer table */}
          <div>
            <h4 style={{ color: '#c4b5fd', marginBottom: '0.75rem', fontSize: '0.9rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              Traffic-Quellen
            </h4>
            {data.referrerStats?.length === 0 ? (
              <p style={{ color: '#64748b', fontStyle: 'italic' }}>Noch keine Daten.</p>
            ) : (
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
                <thead>
                  <tr style={{ color: '#94a3b8', borderBottom: '1px solid rgba(168,85,247,0.3)' }}>
                    <th style={{ textAlign: 'left', padding: '0.5rem 0.75rem' }}>Quelle</th>
                    <th style={{ textAlign: 'right', padding: '0.5rem 0.75rem' }}>Besuche</th>
                    <th style={{ padding: '0.5rem 0.75rem', width: '40%' }}></th>
                  </tr>
                </thead>
                <tbody>
                  {data.referrerStats?.map(r => {
                    const totalRef = data.referrerStats.reduce((s, x) => s + x.visits, 0) || 1;
                    const pct = Math.round((r.visits / totalRef) * 100);
                    return (
                      <tr key={r.source} style={{ borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                        <td style={{ padding: '0.5rem 0.75rem', color: '#e2e8f0' }}>{r.source}</td>
                        <td style={{ padding: '0.5rem 0.75rem', textAlign: 'right', color: '#c4b5fd', fontWeight: 'bold' }}>{r.visits}</td>
                        <td style={{ padding: '0.5rem 0.75rem' }}>
                          <div style={{ background: 'rgba(168,85,247,0.15)', borderRadius: '4px', height: '8px' }}>
                            <div style={{ background: barColor, borderRadius: '4px', height: '8px', width: `${pct}%`, transition: 'width 0.4s' }} />
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </>
      )}
    </div>
  );
}

export default AnalyticsTab;
